/**
 * CRM Conversations (Active / No reply yet / Archived)
 *
 * Conversation-centric lists over the lead inbox data, driven by the
 * denormalized conversation state on crm.contacts (last_inbound_at /
 * last_outbound_at / last_activity_at / archived_at):
 *   - active      : the lead has replied at least once (and isn't archived)
 *   - awaiting    : we've reached out, no response yet (and isn't archived)
 *   - needs_reply : the lead replied and we still owe a response (subset of
 *                   active, server-derived from needs_reply_at)
 *   - archived    : recruiter archived it (an inbound reply auto-reopens)
 * Rows are chat-style cards (avatar, name + study + recency, last-message
 * snippet with a direction arrow) with inline owner assignment and an
 * archive/unarchive action (optimistic removal, re-inserted on failure).
 * Mounted at /crm/active and /crm/awaiting; the active view has an
 * "Awaiting my reply" toggle (default on, narrows to needs_reply) and a
 * "Show archived" toggle that flips the list to the archived set.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Archive,
  ArchiveRestore,
  ArrowDownLeft,
  ArrowUpRight,
  Hourglass,
  Loader2,
  MessagesSquare,
  MoonStar,
  StickyNote,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { ContactListItem, ConversationFilter, LifecycleStats, StudyOption, contactName, studyInternalLabel } from '../types';
import { INCOMING_ELIGIBILITY_OPTIONS, MILESTONE_FILTER_OPTIONS, MILESTONE_LABELS, getPref, initials, needsReply, notifyStatsChanged, prettySlug, relTime, setPref } from '../lib';
import { ChannelIcon, StudyChips } from '../components/shared';
import { ActiveFilters, AddFilterMenu, FilterBar, FilterChip, FilterSelect, MilestoneChip, OwnerSelect, PageHeader, RefreshButton, SearchInput, SnoozeButton, isActiveSnooze } from '../components';
import type { ActiveChip, FilterDef } from '../components';

const LIMIT = 50;

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** Snippet fallback when the last message has no body (calls, status-only rows). */
const channelWord = (channel: string): string => {
  if (channel === 'sms') return 'SMS';
  if (channel === 'call') return 'Call';
  if (channel === 'email') return 'Email';
  return prettySlug(channel);
};

/** Conversation-recency sort key (matches the API's sort=activity ordering). */
const activityTs = (c: ContactListItem): number | null => {
  const iso = c.last_activity_at || c.last_comm?.occurred_at;
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

const COPY: Record<ConversationFilter, { title: string; subtitle: string; emptyTitle: string; emptyHint: string }> = {
  active: {
    title: 'Active conversations',
    subtitle: 'Leads who have replied — keep these moving.',
    emptyTitle: 'No active conversations',
    emptyHint: 'When a lead replies, the conversation lands here.',
  },
  awaiting: {
    title: 'No reply yet',
    subtitle: 'You have reached out; no response yet.',
    emptyTitle: 'Nothing awaiting a reply',
    emptyHint: 'Leads you have contacted but who have not responded yet will appear here.',
  },
  needs_reply: {
    title: 'Awaiting your reply',
    subtitle: "Patients who messaged and haven't heard back yet.",
    emptyTitle: 'All caught up',
    emptyHint: 'No leads are waiting on a reply right now.',
  },
  archived: {
    title: 'Archived',
    subtitle: 'Archived conversations — an inbound reply reopens them automatically.',
    emptyTitle: 'No archived conversations',
    emptyHint: 'Archive a conversation to move it out of the active list.',
  },
};

interface Filters {
  q: string;
  mine: boolean;
  /** study_slug the lead is being considered for ('' = all studies). */
  study: string;
  /** Incoming (landing-form) eligibility verdict ('' = any). */
  eligibility: string;
  /** Derived pertinent-milestone key ('' = any). */
  milestone: string;
  /** Sort order: 'activity' (default, recency), 'eligibility' (incoming verdict),
   *  '' (newest), 'oldest'. */
  sort: string;
  /** Active view only: narrow to leads that owe a recruiter reply (needs_reply).
   *  Defaults on (persisted) so the active view foregrounds waiting patients. */
  needsReplyOnly: boolean;
  /** Active view only: flip the list to the archived set. */
  showArchived: boolean;
  /** Active/No-reply only: reveal leads snoozed to a future time (hidden by default). */
  showSnoozed: boolean;
}

/** localStorage pref key (namespaced under `crm.` by getPref/setPref). */
const NEEDS_REPLY_PREF = 'conversations.needsReplyOnly';

const DEFAULT_FILTERS: Filters = {
  q: '',
  mine: false,
  study: '',
  eligibility: '',
  milestone: '',
  sort: 'activity', // conversations are recency-triaged by default
  // Seed from the saved pref so a recruiter who turns it off stays off.
  needsReplyOnly: getPref(NEEDS_REPLY_PREF, 'true') === 'true',
  showArchived: false,
  showSnoozed: false,
};

/** Per-mode pref key so Active and No-reply keep their own filter sets. */
const FILTERS_PREF = 'conversations.filters';

/** Load this mode's persisted filters, merged over the defaults. */
function loadFilters(mode: 'active' | 'awaiting'): Filters {
  const raw = getPref(`${FILTERS_PREF}.${mode}`, '');
  if (!raw) return DEFAULT_FILTERS;
  try {
    return { ...DEFAULT_FILTERS, ...(JSON.parse(raw) as Partial<Filters>) };
  } catch {
    return DEFAULT_FILTERS;
  }
}

export default function Conversations({ mode }: { mode: 'active' | 'awaiting' }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState<ContactListItem[]>([]);
  const [stats, setStats] = useState<LifecycleStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<Filters>(() => loadFilters(mode));
  const [studies, setStudies] = useState<StudyOption[]>([]);
  const filtersRef = useRef<Filters>(filters);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs mirror props/auth so the stable runQuery callback never reads stale
  // values (assigned during render, so they're current before any effect runs).
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const userIdRef = useRef(user?.id || '');
  userIdRef.current = user?.id || '';
  // Mirrored for toggleRowArchived: after an optimistic removal empties the
  // visible page, it must see the post-removal length and current hasMore.
  const rowCountRef = useRef(0);
  rowCountRef.current = rows.length;
  const hasMoreRef = useRef(false);
  hasMoreRef.current = hasMore;

  // Monotonic request id: a response only commits if it is still the newest
  // query, so a slow earlier fetch can never clobber a newer filter's results
  // and an in-flight Load-more can't append rows from a previous filter.
  const reqSeqRef = useRef(0);

  const viewOf = (m: 'active' | 'awaiting', f: Filters): ConversationFilter =>
    m === 'active' && f.showArchived ? 'archived'
      : m === 'active' && f.needsReplyOnly ? 'needs_reply'
        : m;

  const runQuery = useCallback(async (offset: number, replace: boolean) => {
    const seq = ++reqSeqRef.current;
    if (replace) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }
    const f = filtersRef.current;
    const params: Record<string, string | number> = {
      conversation: viewOf(modeRef.current, f),
      limit: LIMIT,
      offset,
    };
    // 'activity' (the default) and 'eligibility' are sent; '' falls through to
    // the backend's newest-first default.
    if (f.sort) params.sort = f.sort;
    if (f.q.trim()) params.q = f.q.trim();
    if (f.mine && userIdRef.current) params.owner = userIdRef.current;
    if (f.study) params.study = f.study;
    if (f.eligibility) params.eligibility = f.eligibility;
    if (f.milestone) params.milestone = f.milestone;
    // Hide future-snoozed leads unless "Show snoozed" is on. The backend ignores
    // this for the needs_reply + archived views (a waiting patient/an archived
    // lead must stay visible regardless).
    params.show_snoozed = f.showSnoozed ? 'true' : 'false';
    try {
      const res = await api.get('/api/crm/contacts', { params });
      if (seq !== reqSeqRef.current) return; // stale response — a newer query owns the list
      const batch: ContactListItem[] = res.data?.contacts || [];
      setRows((prev) => {
        if (replace) return batch;
        // Dedup on append: a conversation touched between pages shifts the
        // offset and would repeat the page-boundary rows (duplicate keys).
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...batch.filter((b) => !seen.has(b.id))];
      });
      setHasMore(batch.length === LIMIT);
    } catch (e: any) {
      if (seq === reqSeqRef.current) setError(errMsg(e));
    } finally {
      if (seq === reqSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/api/crm/contacts/stats');
      setStats(res.data || null);
    } catch {
      // Tolerate silently — the header renders without a count.
    }
  }, []);

  // Studies for the filter dropdown — fetched once. Tolerate failure silently:
  // the dropdown just stays empty (showing only "All studies") and the rest of
  // the page keeps working.
  useEffect(() => {
    let active = true;
    api.get('/api/crm/studies')
      .then((res) => { if (active) setStudies(res.data?.studies || []); })
      .catch(() => { /* no-op */ });
    return () => { active = false; };
  }, []);

  // Route (mode) change: load the target mode's persisted filters, reset the
  // list, re-query from page 0. (Also runs on mount, seeding the first fetch.)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const f = loadFilters(mode);
    filtersRef.current = f;
    setFilters(f);
    setRows([]);
    setHasMore(false);
    runQuery(0, true);
    loadStats();
  }, [mode, runQuery, loadStats]);

  // Stats refresh after an archive/unarchive is debounced: rapid triage would
  // otherwise re-fire the stats endpoint once per row.
  const statsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
  }, []);

  /** Apply a filter change and re-query page 0 (text inputs debounce 300ms). */
  const apply = (patch: Partial<Filters>, debounce = false) => {
    const next = { ...filtersRef.current, ...patch };
    filtersRef.current = next;
    setFilters(next);
    setPref(`${FILTERS_PREF}.${mode}`, JSON.stringify(next)); // survive navigation
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (debounce) {
      debounceRef.current = setTimeout(() => runQuery(0, true), 300);
    } else {
      runQuery(0, true);
    }
  };

  const loadMore = () => runQuery(rows.length, false);
  const refresh = () => { runQuery(0, true); loadStats(); };

  const view = viewOf(mode, filters);
  const copy = COPY[view];
  // active/awaiting counts always exclude snoozed server-side; while "Show
  // snoozed" reveals them in the list, hide the header count to avoid showing
  // fewer than the visible rows. (needs_reply/archived counts are unaffected.)
  const count = stats?.conversations && !(filters.showSnoozed && (view === 'active' || view === 'awaiting'))
    ? stats.conversations[view]
    : null;
  const HeaderIcon = view === 'archived' ? Archive : mode === 'active' ? MessagesSquare : Hourglass;
  const hasFilters = !!(filters.q.trim() || filters.mine || filters.study || filters.eligibility || filters.milestone);

  /** Locally shift the sidebar/header counts when a row moves between tabs. */
  const bumpConv = (from: ConversationFilter, to: ConversationFilter) => {
    setStats((s) => {
      if (!s || !s.conversations) return s;
      const next = { ...s.conversations };
      next[from] = Math.max(0, (next[from] ?? 0) - 1);
      next[to] = (next[to] ?? 0) + 1;
      return { ...s, conversations: next };
    });
  };

  /**
   * Archive/unarchive a row: optimistic removal from the current list; on
   * failure the row is re-inserted in conversation-recency order and the
   * error banner shows. Stats are bumped locally and refetched lazily.
   */
  const toggleRowArchived = async (row: ContactListItem, archive: boolean) => {
    const from: ConversationFilter = archive ? view : 'archived';
    // Unarchive destination: a contact with no outbound comms belongs to
    // neither tab, so skip the local count bump (the refetch reconciles).
    const to: ConversationFilter | null = archive
      ? 'archived'
      : row.last_inbound_at ? 'active' : row.last_outbound_at ? 'awaiting' : null;
    setError(null);
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    if (to) bumpConv(from, to);
    try {
      await api.patch(`/api/crm/contacts/${row.id}`, { archived: archive });
      notifyStatsChanged();
      if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
      statsDebounceRef.current = setTimeout(loadStats, 1500);
      // Don't strand the recruiter: if the optimistic removal emptied the
      // visible page while more rows exist, pull the next page in.
      if (rowCountRef.current === 0 && hasMoreRef.current) runQuery(0, true);
    } catch (e: any) {
      setRows((prev) => {
        if (prev.some((r) => r.id === row.id)) return prev;
        const next = [...prev, row];
        next.sort((a, b) => {
          const ta = activityTs(a);
          const tb = activityTs(b);
          if (ta === tb) return 0;
          if (ta === null) return 1; // nulls last
          if (tb === null) return -1;
          return tb - ta;
        });
        return next;
      });
      if (to) bumpConv(to, from); // undo the optimistic count shift
      setError(errMsg(e));
    }
  };

  const onOwnerChanged = (id: string, owner: string | null) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, owner_user_id: owner } : r)));

  /**
   * Per-row snooze: defer a lead to a future time (or un-snooze with null). In
   * the active/awaiting views (which hide snoozed by default) a future snooze
   * removes the row and decrements the tab count; with "Show snoozed" on, or on
   * un-snooze, snoozed_until updates in place. needs_reply/archived never hide
   * (enforced server-side) and don't render the snooze action.
   */
  const snoozeRow = async (row: ContactListItem, untilIso: string | null) => {
    const inFilteredView = view === 'active' || view === 'awaiting';
    const wasSnoozed = isActiveSnooze(row.snoozed_until);
    const nowSnoozed = isActiveSnooze(untilIso);
    const hiding = nowSnoozed && !filters.showSnoozed && inFilteredView;
    setError(null);
    if (hiding) {
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } else {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, snoozed_until: untilIso } : r)));
    }
    // active/awaiting badge counts always exclude snoozed server-side, so mirror
    // a snooze-state transition both ways (the debounced loadStats reconciles).
    if (inFilteredView && wasSnoozed !== nowSnoozed) {
      const delta = nowSnoozed ? -1 : 1;
      setStats((s) => (s && s.conversations
        ? { ...s, conversations: { ...s.conversations, [view]: Math.max(0, (s.conversations[view] ?? 0) + delta) } }
        : s));
    }
    try {
      await api.patch(`/api/crm/contacts/${row.id}`, { snoozed_until: untilIso });
      notifyStatsChanged();
      if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
      statsDebounceRef.current = setTimeout(loadStats, 1500);
      if (hiding && rowCountRef.current === 0 && hasMoreRef.current) runQuery(0, true);
    } catch (e: any) {
      runQuery(0, true); // simplest reliable revert
      loadStats();
      setError(errMsg(e));
    }
  };

  // ---- Unified filter system (shared <FilterBar>) ----
  const studyLabel = (slug: string) => {
    const s = studies.find((x) => x.slug === slug);
    return s ? studyInternalLabel(s) || prettySlug(s.slug) : prettySlug(slug);
  };

  // Reset only the filtering dimensions (search + secondary filters + Mine),
  // preserving sort and the view-defining toggles (which conversation view
  // you're in: needs_reply / archived / show-snoozed).
  const clearFilters = () => apply({ q: '', mine: false, study: '', eligibility: '', milestone: '' });

  // Secondary filters, revealed by the "Add filter" popover.
  const moreFilters: FilterDef[] = [
    ...(studies.length > 0
      ? [{
        key: 'study',
        label: 'Study',
        value: filters.study,
        onChange: (v: string) => apply({ study: v }),
        options: [
          { value: '', label: 'All studies' },
          ...studies.map((s) => ({
            value: s.slug,
            label: (studyInternalLabel(s) || prettySlug(s.slug)) + (typeof s.count === 'number' ? ` (${s.count})` : ''),
          })),
        ],
      } as FilterDef]
      : []),
    {
      key: 'eligibility',
      label: 'Incoming eligibility',
      value: filters.eligibility,
      onChange: (v) => apply({ eligibility: v }),
      title: "Best landing-form verdict across the lead's studies. Manually-added contacts (no form submission) aren't matched.",
      options: [{ value: '', label: 'Any eligibility' }, ...INCOMING_ELIGIBILITY_OPTIONS],
    },
    {
      key: 'milestone',
      label: 'Milestone',
      value: filters.milestone,
      onChange: (v) => apply({ milestone: v }),
      title: 'The pertinent-milestone sub-line shown under each lead.',
      options: [
        { value: '', label: 'Any milestone' },
        ...MILESTONE_FILTER_OPTIONS.map((k) => ({ value: k as string, label: MILESTONE_LABELS[k] })),
      ],
    },
  ];

  // Count only the popover filters that actually constrain results.
  const moreFiltersCount =
    (filters.study ? 1 : 0) +
    (filters.eligibility ? 1 : 0) +
    (filters.milestone ? 1 : 0);

  // Applied-filter summary chips. The visible Mine quick chip already covers
  // itself, so only the secondary filters surface here.
  const activeChips: ActiveChip[] = [];
  if (filters.study) activeChips.push({ key: 'study', label: `Study: ${studyLabel(filters.study)}`, onRemove: () => apply({ study: '' }) });
  if (filters.eligibility) {
    const el = INCOMING_ELIGIBILITY_OPTIONS.find((o) => o.value === filters.eligibility);
    activeChips.push({ key: 'eligibility', label: `Eligibility: ${el?.label || filters.eligibility}`, onRemove: () => apply({ eligibility: '' }) });
  }
  if (filters.milestone) {
    activeChips.push({
      key: 'milestone',
      label: `Milestone: ${MILESTONE_LABELS[filters.milestone as keyof typeof MILESTONE_LABELS] || filters.milestone}`,
      onRemove: () => apply({ milestone: '' }),
    });
  }

  return (
    <div className="space-y-4">
      <PageHeader
        icon={HeaderIcon}
        title={copy.title}
        count={count ?? null}
        subtitle={copy.subtitle}
        actions={<RefreshButton onClick={refresh} loading={loading} label="Refresh conversations" />}
      />

      {/* Toolbar: search + quick chips + Add-filter popover + active-filter chips */}
      <FilterBar active={<ActiveFilters chips={activeChips} onClearAll={hasFilters ? clearFilters : undefined} />}>
        <SearchInput
          value={filters.q}
          onChange={(v) => apply({ q: v }, true)}
          placeholder="Search name, phone, or email…"
          label="Search conversations"
          className="flex-1 basis-56"
        />
        {!!user?.id && (
          <FilterChip active={filters.mine} onClick={() => apply({ mine: !filters.mine })}>Mine</FilterChip>
        )}
        {mode === 'active' && !filters.showArchived && (
          <FilterChip
            active={filters.needsReplyOnly}
            onClick={() => {
              const nextOn = !filters.needsReplyOnly;
              setPref(NEEDS_REPLY_PREF, String(nextOn));
              apply({ needsReplyOnly: nextOn });
            }}
            icon={<MessagesSquare className="h-4 w-4" />}
          >
            Awaiting my reply
          </FilterChip>
        )}
        {mode === 'active' && (
          <FilterChip
            active={filters.showArchived}
            onClick={() => apply({ showArchived: !filters.showArchived })}
            icon={<Archive className="h-4 w-4" />}
          >
            Show archived
          </FilterChip>
        )}
        {(view === 'active' || view === 'awaiting') && (
          <FilterChip
            active={filters.showSnoozed}
            onClick={() => apply({ showSnoozed: !filters.showSnoozed })}
            icon={<MoonStar className="h-4 w-4" />}
          >
            Show snoozed
          </FilterChip>
        )}
        <FilterSelect value={filters.sort} onChange={(v) => apply({ sort: v })} auto label="Sort order">
          <option value="activity">Recent activity</option>
          <option value="eligibility">By eligibility</option>
          <option value="">Newest first</option>
          <option value="oldest">Oldest first</option>
        </FilterSelect>
        <AddFilterMenu filters={moreFilters} activeCount={moreFiltersCount} />
      </FilterBar>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">{error}</div>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary-600" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <HeaderIcon className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          <h3 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">{copy.emptyTitle}</h3>
          <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
            {hasFilters ? 'Try clearing your search, study, or the Mine filter.' : copy.emptyHint}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {rows.map((c) => {
              const reply = needsReply(c.last_comm);
              const lc = c.last_comm;
              const isArchivedView = view === 'archived';
              return (
                <div
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/crm/contacts/${c.id}`)}
                  onKeyDown={(e) => {
                    // Only when the row itself is focused — Enter/Space on an
                    // embedded select/button must not navigate away.
                    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                      if (e.key === ' ') e.preventDefault(); // don't scroll the page
                      navigate(`/crm/contacts/${c.id}`);
                    }
                  }}
                  className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/50 sm:p-4"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
                  >
                    {initials(contactName(c))}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        title={contactName(c)}
                        className={`truncate text-sm text-gray-900 dark:text-gray-100 ${reply ? 'font-semibold' : 'font-medium'}`}
                      >
                        {contactName(c)}
                      </span>
                      {reply && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" title="Needs your reply" />}
                      {view !== 'archived' && isActiveSnooze(c.snoozed_until) && (
                        <span className="flex-shrink-0 rounded bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">snoozed</span>
                      )}
                      {(c.studies || []).length > 0 && (
                        <span className="min-w-0"><StudyChips studies={c.studies} max={1} /></span>
                      )}
                      <span className="ml-auto flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
                        {relTime(c.last_activity_at || lc?.occurred_at)}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                      {lc ? (
                        lc.channel === 'note' ? (
                          // Internal note — never sent to the lead, so no direction arrow.
                          <>
                            <StickyNote aria-hidden="true" className="h-4 w-4 flex-shrink-0 text-amber-500" />
                            <span className="truncate">{lc.body ? `Note: ${lc.body}` : channelWord(lc.channel)}</span>
                          </>
                        ) : (
                          <>
                            {lc.direction === 'inbound' ? (
                              <ArrowDownLeft aria-hidden="true" className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                            ) : (
                              <ArrowUpRight aria-hidden="true" className="h-4 w-4 flex-shrink-0 text-gray-400" />
                            )}
                            {!lc.body && <ChannelIcon channel={lc.channel} />}
                            <span className="truncate">{lc.body || channelWord(lc.channel)}</span>
                          </>
                        )
                      ) : (
                        <span className="truncate text-gray-400 dark:text-gray-500">No messages yet</span>
                      )}
                    </div>
                    {c.milestone && <div className="mt-1"><MilestoneChip milestone={c.milestone} /></div>}
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    {/* Bound the select's width so the name/snippet keep room at 375px. */}
                    <span className="inline-flex min-w-0 max-w-[7.5rem] sm:max-w-[11rem]">
                      <OwnerSelect
                        contactId={c.id}
                        value={c.owner_user_id}
                        compact
                        onChanged={(o) => onOwnerChanged(c.id, o)}
                      />
                    </span>
                    {view !== 'archived' && (
                      <SnoozeButton
                        snoozedUntil={c.snoozed_until}
                        onSnooze={(iso) => snoozeRow(c, iso)}
                        label={contactName(c)}
                      />
                    )}
                    <button
                      onClick={() => toggleRowArchived(c, !isArchivedView)}
                      aria-label={isArchivedView ? 'Unarchive conversation' : 'Archive conversation'}
                      title={isArchivedView ? 'Unarchive' : 'Archive conversation'}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:h-8 sm:w-8"
                    >
                      {isArchivedView ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div className="flex justify-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
              >
                {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
